import { ShippingInfoStoryblok } from 'storyblok-types';
import { hasOnlyProductsFromCategory } from 'pages/Cart/Sidebar/utils';
import { divide, multiply, round, subtract } from 'modules/math';
import { useCart } from 'modules/shop/cart/hooks';
import { useSiteConfig } from 'modules/shop/siteConfig/hooks';
import { GIFT_CARD_CATEGORY } from 'commerce/scayle/constants';
import { BasketResponse } from 'commerce/scayle/user-interaction/types';

export type ShippingProgressBarConfig = {
  percentageUntilFreeShipping: number;
  priceLeftForFreeShipping: number;
  shippingPrice: number;
  hasOnlyGiftCards: boolean;
};

export const useShippingProgressBarRaw = (
  cart: BasketResponse | null,
  shipping?: ShippingInfoStoryblok,
): ShippingProgressBarConfig => {
  const hasOnlyGiftCards = hasOnlyProductsFromCategory(
    GIFT_CARD_CATEGORY,
    cart?.items,
  );

  const totalPrice = divide(
    cart?.items?.reduce(
      (sum, item) => sum + (item.price?.total.withTax || 0) * item.quantity,
      0,
    ) || 0,
    100,
  );

  const shippingCosts = (shipping?.prices?.tbody || [])
    .map((entry) => ({
      totalPrice: Number(entry?.body?.[0]?.value) || 0,
      shippingPrice: multiply(Number(entry?.body?.[1]?.value) || 0, 100),
    }))
    .sort((a, b) => subtract(a.totalPrice, b.totalPrice));

  let shippingPrice = hasOnlyGiftCards
    ? 0
    : shippingCosts?.[0]?.shippingPrice || 0;

  if (!hasOnlyGiftCards) {
    shippingCosts?.forEach((cost) => {
      if (cost.totalPrice <= totalPrice) {
        shippingPrice = cost.shippingPrice;
      }
    });
  }

  const highestTotalPrice = shippingCosts.at(-1)?.totalPrice || 0;

  const priceLeftForFreeShipping = round(
    multiply(Math.max(subtract(highestTotalPrice, totalPrice), 0), 100),
    2,
  );

  const percentageUntilFreeShipping =
    highestTotalPrice > 0
      ? round(multiply(divide(totalPrice, highestTotalPrice), 100), 2)
      : 0;

  return {
    percentageUntilFreeShipping,
    priceLeftForFreeShipping,
    shippingPrice,
    hasOnlyGiftCards,
  };
};

export const useShippingProgressBar = (): ShippingProgressBarConfig => {
  const { cart } = useCart();
  const config = useSiteConfig();

  return useShippingProgressBarRaw(cart, config.shipping);
};
